import { Gem, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TooltipWrapper } from "components/permissions/permissions";
import { useState } from "react";
import { Attribute } from "../../models";
import { CatalogueProduct, LayerGroup } from "../../catalogue/catalogue";
import {
  getChildProducts,
  getGroupState,
  getLayerGroups,
  getProductState,
  getSelectedLayerConfigs,
  isProductEnabled,
  toCheckedState,
} from "../../catalogue/catalogue-utils";
import { IncludedLayersTooltip } from "../../components/included-layers-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AttributeTreeProps {
  products: CatalogueProduct[];
  // Full catalogue — needed to resolve child/parent/dependency relationships.
  catalogue: CatalogueProduct[];
  selected: Attribute[];
  search: string;
  onToggleProduct: (product: CatalogueProduct, checked: boolean) => void;
  onToggleLayer: (
    product: CatalogueProduct,
    configName: string,
    checked: boolean,
  ) => void;
  onToggleGroup: (
    product: CatalogueProduct,
    group: LayerGroup,
    checked: boolean,
  ) => void;
}

export const AttributeTree = (props: AttributeTreeProps) => {
  // Track manual open state per product key
  const [manualOpen, setManualOpen] = useState<Record<string, boolean>>({});

  const searchTerm = props.search.trim().toLowerCase();
  const hasSearch = searchTerm.length > 0;

  return (
    <div className="flex flex-col ml-1">
      {props.products.map((product) => {
        const state = getProductState(product, props.selected);
        const selectedConfigs = getSelectedLayerConfigs(
          product,
          props.selected,
        );
        // Built once per product: the group rows below each need to test
        // membership, and rebuilding it per group repeated the same scan.
        const chosenConfigs = new Set(selectedConfigs);
        const childProducts = getChildProducts(product, props.catalogue);
        // Per-group products (G-NAF Core, Electoral Boundaries, ABS) show one row
        // per billable group, each with its own credit badge, instead of raw
        // layers.
        const groups = getLayerGroups(product);
        // Single-unit products bill as one charge and bundle their companion
        // layers, so they render as one non-expandable row (the layers-included
        // hover conveys the contents). Everything else expands to show its layers
        // or billable groups.
        const isExpandable =
          !product.singleUnit &&
          (product.layers.length > 1 || childProducts.length > 0);
        const safeName = product.productName.replaceAll(" ", "-");

        // Check if product name matches search
        const productMatches = product.productName
          .toLowerCase()
          .includes(searchTerm);

        // Filter layers based on search
        const visibleLayers = hasSearch
          ? productMatches
            ? product.layers // Show all layers if product name matched
            : product.layers.filter((layer) =>
                layer.layerName.toLowerCase().includes(searchTerm),
              )
          : product.layers;

        // Sub-products follow the same search rule: show all if the parent
        // matched, otherwise only those whose own name matches.
        const visibleChildren = hasSearch
          ? productMatches
            ? childProducts
            : childProducts.filter((child) =>
                child.productName.toLowerCase().includes(searchTerm),
              )
          : childProducts;

        // Control collapsible open state
        const isOpen = hasSearch || (manualOpen[product.productKey] ?? false);

        // Products with a requiresProductKey are disabled until the required
        // product is selected.
        const enabled = isProductEnabled(product, props.selected);
        const requiredProductName = product.requiresProductKey
          ? props.catalogue.find(
              (pr) => pr.productKey === product.requiresProductKey,
            )?.productName
          : undefined;

        const productCheckbox = (
          <Checkbox
            id={`checkbox-${safeName}`}
            className={`shrink-0 after:hidden ${!enabled ? "pointer-events-none" : ""}`}
            disabled={!enabled}
            checked={toCheckedState(state)}
            onCheckedChange={(checked) =>
              props.onToggleProduct(product, !!checked)
            }
          />
        );

        // Checkbox + label as one unit. When the product is disabled by a
        // dependency, the whole unit is wrapped in a tooltip so hovering
        // anywhere across the row (checkbox or label) explains why.
        const productControls = (
          <>
            {productCheckbox}
            <label
              htmlFor={enabled ? `checkbox-${safeName}` : undefined}
              className={`flex flex-1 min-w-0 items-center gap-1 py-0.5 pl-1.5 ${
                enabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"
              }`}
            >
              <span className="flex-1 min-w-0 truncate text-sm">
                {product.productName}
              </span>
              {/* Per-group products surface layers + credits on their group
                  rows instead, so the product row omits the icon and badge. */}
              {groups.length === 0 && product.layers.length > 1 && (
                <IncludedLayersTooltip
                  layerNames={product.layers.map((l) => l.layerName)}
                  heading={`Includes ${product.layers.length} layers`}
                  preventLabelToggle
                />
              )}
              {product.isPremium && (
                <TooltipWrapper title="Premium Data">
                  <Gem className="size-4 shrink-0 text-blue-400" />
                </TooltipWrapper>
              )}
              {groups.length === 0 && (
                <span className="text-xs text-muted-foreground shrink-0">
                  {product.credits} Credits
                </span>
              )}
            </label>
          </>
        );

        const productRow = enabled ? (
          <div className="flex flex-1 min-w-0 items-center">
            {productControls}
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex flex-1 min-w-0 items-center">
                {productControls}
              </div>
            </TooltipTrigger>
            <TooltipContent className="z-2000">
              {`Requires ${requiredProductName ?? "another product"}`}
            </TooltipContent>
          </Tooltip>
        );

        return (
          <div key={product.productKey}>
            {isExpandable ? (
              <Collapsible
                open={isOpen}
                onOpenChange={(open) =>
                  setManualOpen((prev) => ({
                    ...prev,
                    [product.productKey]: open,
                  }))
                }
              >
                <div className="flex items-center">
                  <CollapsibleTrigger className="flex size-6 shrink-0 items-center justify-center rounded hover:cursor-pointer hover:bg-accent hover:text-accent-foreground [&[data-state=open]>svg]:rotate-90">
                    <ChevronRight className="size-3.5 transition-transform" />
                  </CollapsibleTrigger>
                  {productRow}
                </div>
                <CollapsibleContent className="flex flex-col pl-6">
                  {groups.length > 0
                    ? groups.map((group) => (
                        <label
                          key={group.label}
                          className={`flex items-center gap-1 py-1 ${
                            enabled
                              ? "cursor-pointer"
                              : "cursor-not-allowed opacity-50"
                          }`}
                        >
                          <Checkbox
                            id={`checkbox-group-${group.label.replaceAll(" ", "-")}`}
                            disabled={!enabled}
                            className={`shrink-0 ${!enabled ? "pointer-events-none" : ""}`}
                            checked={toCheckedState(
                              getGroupState(group, chosenConfigs),
                            )}
                            onCheckedChange={(checked) =>
                              props.onToggleGroup(product, group, !!checked)
                            }
                          />
                          <span className="flex-1 min-w-0 truncate text-sm">
                            {group.label}
                          </span>
                          {/* Only bundled units get a hover explaining what
                              they include; a single-layer unit is self-evident. */}
                          {group.layers.length > 1 && (
                            <IncludedLayersTooltip
                              layerNames={group.layers.map((l) => l.layerName)}
                              preventLabelToggle
                            />
                          )}
                          <span className="text-xs text-muted-foreground shrink-0">
                            {group.credits} Credits
                          </span>
                        </label>
                      ))
                    : visibleLayers.map((layer) => (
                        <label
                          key={layer.configName}
                          className={`flex items-center gap-1 py-1 ${
                            enabled
                              ? "cursor-pointer"
                              : "cursor-not-allowed opacity-50"
                          }`}
                        >
                          <Checkbox
                            id={`checkbox-layer-${layer.configName}`}
                            disabled={!enabled}
                            className={!enabled ? "pointer-events-none" : ""}
                            checked={selectedConfigs.includes(layer.configName)}
                            onCheckedChange={(checked) =>
                              props.onToggleLayer(
                                product,
                                layer.configName,
                                !!checked,
                              )
                            }
                          />
                          <span className="text-sm">{layer.layerName}</span>
                        </label>
                      ))}
                  {visibleChildren.map((child) => (
                    <label
                      key={child.productKey}
                      className="flex items-center gap-1 py-1 cursor-pointer"
                    >
                      <Checkbox
                        id={`checkbox-${child.productName.replaceAll(" ", "-")}`}
                        checked={
                          getProductState(child, props.selected) !== "none"
                        }
                        onCheckedChange={(checked) =>
                          props.onToggleProduct(child, !!checked)
                        }
                      />
                      <span className="flex-1 min-w-0 truncate text-sm">
                        {child.productName}
                      </span>
                      {child.isPremium && (
                        <TooltipWrapper title="Premium Data">
                          <Gem className="size-4 shrink-0 text-blue-600" />
                        </TooltipWrapper>
                      )}
                      <Badge variant="outline" className="text-xs shrink-0">
                        {child.credits} Credits
                      </Badge>
                    </label>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <div className="flex items-center">
                {/* Spacer matching the chevron trigger footprint (size-6) so
                    leaf checkboxes line up with expandable rows. */}
                <span className="size-6 shrink-0" />
                {productRow}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
